import {ActionIcon, Group, Menu, Table, TextInput, Tooltip} from "@mantine/core";
import {IconLanguage, IconSearch, IconTrash} from "@tabler/icons-react";
import {useCallback, useState} from "react";
import {toHiragana, toKatakana} from "wanakana";
import {getKanjiSuggestions} from "../lib/kanjiApi";
import type {BatchRow} from "../types";

export function BatchEntryRowJapaneseCharacter(props: {
  row: BatchRow;
  onUpdate: (id: string, field: keyof BatchRow, value: string) => void;
  onRemove: (id: string) => void;
}) {
  const {row, onUpdate, onRemove} = props;

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const _handleSuggestKanji = useCallback(async () => {
    const textToSearch = row.expression;
    if (!textToSearch) return;

    setIsSearching(true);
    const hira = toHiragana(textToSearch);
    const results = await getKanjiSuggestions(hira);
    setSuggestions(results);
    setIsSearching(false);
  }, [row.expression]);

  const handleTransliterate = useCallback(
    (field: keyof BatchRow) => {
      if (!row?.[field]) return;

      let newValue = row[field];
      if (field === "field1") {
        newValue = toKatakana(row[field]);
      } else if (field === "field2") {
        newValue = toHiragana(row[field]);
      }
      onUpdate(row.id, field, newValue);
    },
    [row, onUpdate],
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <TextInput
            value={row.expression}
            onChange={e => onUpdate(row.id, "expression", e.target.value)}
            placeholder="猫"
            variant="unstyled"
            required={row.meaning !== ""}
            style={{flexGrow: 1}}
          />
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle" loading={isSearching} type="button">
                <IconSearch size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Suggestions</Menu.Label>
              {suggestions.length > 0 ? (
                suggestions.map(s => (
                  <Menu.Item key={s} onClick={() => onUpdate(row.id, "expression", s)}>
                    {s}
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item disabled>No suggestions</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <TextInput
            value={row.field1}
            onChange={e => onUpdate(row.id, "field1", e.target.value)}
            placeholder="ビン"
            variant="unstyled"
            style={{flexGrow: 1}}
          />
          {row.field1 && (
            <Tooltip label="To Katakana">
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => handleTransliterate("field1")}
              >
                <IconLanguage size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <TextInput
            value={row.field2}
            onChange={e => onUpdate(row.id, "field2", e.target.value)}
            placeholder="ねこ"
            variant="unstyled"
            style={{flexGrow: 1}}
          />
          {row.field2 && (
            <Tooltip label="To Hiragana">
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => handleTransliterate("field2")}
              >
                <IconLanguage size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.meaning}
          onChange={e => onUpdate(row.id, "meaning", e.target.value)}
          placeholder="Cat"
          variant="unstyled"
          required={row.expression !== ""}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          value={row.auxiliary}
          onChange={e => onUpdate(row.id, "auxiliary", e.target.value)}
          placeholder="Radical: 犬, Grade 6"
          variant="unstyled"
        />
      </Table.Td>
      <Table.Td>
        <ActionIcon
          color="red"
          variant="subtle"
          onClick={() => onRemove(row.id)}
          title="Remove row"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
}
